import { Component } from '@theme/component';

/**
 * @typedef {Object} ReadMoreContentRefs
 * @property {HTMLButtonElement} showMoreButton
 * @property {HTMLElement} introContent
 *
 * @extends {Component<ReadMoreContentRefs>}
 */
class ReadMoreContentComponent extends Component {
  requiredRefs = ['showMoreButton', 'introContent'];

  #expanded = false;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #resizeTimeout;

  connectedCallback() {
    super.connectedCallback();
    this.#evaluateTruncation();
    window.addEventListener('resize', this.#onResize);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#onResize);
    if (this.#resizeTimeout) clearTimeout(this.#resizeTimeout);
  }

  /**
   * Toggles between the collapsed and expanded state. The `--truncatable` class is
   * kept in both states so the toggle stays visible (as "show less" when expanded).
   * @param {Event} event
   */
  toggle = (event) => {
    event.preventDefault();

    this.#expanded = !this.#expanded;
    this.dataset.expanded = String(this.#expanded);
    this.refs.showMoreButton.setAttribute('aria-expanded', String(this.#expanded));

    // Re-check overflow when collapsing so the toggle disappears if it is no longer needed.
    if (!this.#expanded) this.#evaluateTruncation();
  };

  #onResize = () => {
    if (this.#expanded) return;
    if (this.#resizeTimeout) clearTimeout(this.#resizeTimeout);
    this.#resizeTimeout = setTimeout(() => this.#evaluateTruncation(), 150);
  };

  #evaluateTruncation() {
    if (this.#expanded) return;

    this.dataset.expanded = 'false';
    this.refs.showMoreButton.setAttribute('aria-expanded', 'false');

    const target = this.#getTargetNode();
    if (!target) {
      this.classList.remove('read-more-content--truncatable');
      return;
    }

    const isOverflowing = target.scrollHeight > target.clientHeight + 1;
    if (isOverflowing) {
      this.classList.add('read-more-content--truncatable');
    } else {
      this.classList.remove('read-more-content--truncatable');
    }
  }

  #getTargetNode() {
    const content = this.refs.introContent;
    if (!content) return null;

    // Height mode caps the whole inner container, so it is the overflow target.
    if (this.dataset.clampMode === 'height') return content;

    // Lines mode clamps the trailing text node.
    const directNode = content.querySelector('.text-block:last-child > *:last-child');
    if (directNode) return directNode;

    return content.querySelector('.text-block:last-child');
  }
}

if (!customElements.get('read-more-content')) {
  customElements.define('read-more-content', ReadMoreContentComponent);
}
